import { useEffect, useEffectEvent, useLayoutEffect, useRef } from "react";
import { logger } from "@/lib/logger";
import {
  findScrollContainer,
  calculateScrollOffset,
  scrollRangeIntoView,
} from "../utils/scrollUtils";
import { generateAlternativeIds } from "../utils/idFormatUtils";
import type { HighlightName } from "../utils/highlightRegistry";
import { getCommonAncestorOfElements } from "../utils/referenceHighlightDom";
import {
  DOC_HIGHLIGHT_CLASS,
  REFERENCE_SIDEBAR_PREVIEW_BLOCK_CLASS,
} from "../utils/referenceHighlightConstants";
import { findMatchRangeInHaystack } from "../utils/referenceMatchLocate";
import { getVisibleTextSnapshot } from "../utils/uniqueReferenceChunk";
import { liveRangeFor, staticRangesPerNode } from "../utils/visibleTextRanges";
import { useHighlightOwner } from "./useHighlightOwner";

const REFERENCE_HIGHLIGHT_NAMES = [
  "reference",
  "reference-preview",
] as const satisfies readonly HighlightName[];

/** `reference` for an opened Reference, `reference-preview` for the sidebar hover preview. */
export type ReferenceHighlightName = (typeof REFERENCE_HIGHLIGHT_NAMES)[number];

/**
 * The passage as one `StaticRange` per visible text node, so deleted text
 * inside it stays unlit. Located in the visible text of the referenced blocks
 * (their common ancestor), which is the text space the stored match and chunk
 * were computed in. Empty when the passage is not in the document.
 */
function referencePassageRanges(
  docRoot: HTMLElement,
  highlightIds: readonly string[],
  matchTrimmed: string,
  chunkTrimmed: string
): StaticRange[] {
  const elements = highlightIds
    .map((id) => document.getElementById(id))
    .filter((n): n is HTMLElement => n !== null);
  const scope =
    elements.length > 0
      ? getCommonAncestorOfElements(elements, docRoot)
      : docRoot;
  const snapshot = getVisibleTextSnapshot(scope);
  const span = findMatchRangeInHaystack(
    snapshot.fullText,
    matchTrimmed,
    chunkTrimmed || undefined
  );
  return span ? staticRangesPerNode(snapshot, span) : [];
}

/**
 * Highlights the passage a Reference points at with the CSS Custom Highlight
 * API (ADR 0001), under `highlightName`, and scrolls to it. The ranges are
 * rebuilt whenever the document's text nodes may have been replaced
 * (`textIdentity`), before paint, so the highlight survives references
 * loading, consolidation text arriving and reference anchors swapping tags.
 * References that only know their block ids get a block-level class instead.
 */
export function useScrollAndHighlight(
  docRootRef: React.RefObject<HTMLDivElement | null>,
  /** A new value whenever React may have replaced the document's text nodes. */
  textIdentity: unknown,
  isContentRendered: boolean,
  highlightIds?: string[],
  scrollToId?: string,
  highlightMatchText?: string,
  highlightChunkText?: string,
  highlightName: ReferenceHighlightName = "reference",
  /** Block highlight class; defaults to {@link DOC_HIGHLIGHT_CLASS}. */
  blockHighlightClassName?: string,
  /** When true (e.g. multi-link popup preview), highlights apply but the doc does not scroll to the match. */
  suppressScrollToMatch?: boolean
): void {
  const prevHighlightsRef = useRef<string[]>([]);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollRetryCountRef = useRef<number>(0);
  const highlights = useHighlightOwner();
  /** The passage's ranges in the newest parse. */
  const passageRangesRef = useRef<StaticRange[]>([]);
  const matchTrimmed = highlightMatchText?.trim() ?? "";
  const chunkTrimmed = highlightChunkText?.trim() ?? "";

  const paintPassage = useEffectEvent(() => {
    const docRoot = docRootRef.current;
    const ranges =
      docRoot && matchTrimmed
        ? referencePassageRanges(
            docRoot,
            (highlightIds ?? []).filter(Boolean),
            matchTrimmed,
            chunkTrimmed
          )
        : [];
    passageRangesRef.current = ranges;
    for (const name of REFERENCE_HIGHLIGHT_NAMES) {
      if (name === highlightName) {
        highlights.set(name, ranges);
      } else {
        highlights.clear(name);
      }
    }
  });

  // Ids as a key: the renderer passes a fresh array during the sidebar preview.
  const highlightIdsKey = (highlightIds ?? []).join(" ");

  // A layout effect, so a stale range over rewritten text never paints.
  useLayoutEffect(() => {
    paintPassage();
  }, [
    textIdentity,
    highlightIdsKey,
    matchTrimmed,
    chunkTrimmed,
    highlightName,
  ]);

  useEffect(() => {
    const docRoot = docRootRef.current;
    if (!docRoot) return;

    /** Reference URLs carry match/chunk; block-level `.doc-highlight` on whole divs is redundant and noisy. */
    const suppressBlockHighlight =
      Boolean(matchTrimmed) || Boolean(chunkTrimmed);

    const waitForContent = Boolean(scrollToId) || Boolean(matchTrimmed);
    if (waitForContent && !isContentRendered) return;

    const findElementWithAlternatives = (
      id: string
    ): { element: HTMLElement | null; usedId: string | null } => {
      const originalElement = document.getElementById(id);
      if (originalElement) {
        return { element: originalElement, usedId: id };
      }

      const alternatives = generateAlternativeIds(id);
      for (const altId of alternatives) {
        const altElement = document.getElementById(altId);
        if (altElement) {
          return { element: altElement, usedId: altId };
        }
      }

      return { element: null, usedId: null };
    };

    const blockClass = blockHighlightClassName ?? DOC_HIGHLIGHT_CLASS;

    const removeBlockHighlightClasses = (el: HTMLElement) => {
      el.classList.remove(
        DOC_HIGHLIGHT_CLASS,
        REFERENCE_SIDEBAR_PREVIEW_BLOCK_CLASS
      );
    };

    const applyBlockHighlights = () => {
      for (const id of prevHighlightsRef.current) {
        const el = document.getElementById(id);
        if (el) removeBlockHighlightClasses(el);
      }
      const current = (highlightIds || []).filter(Boolean);
      if (!suppressBlockHighlight) {
        for (const id of current) {
          const el = document.getElementById(id);
          if (el) el.classList.add(blockClass);
        }
      }
      prevHighlightsRef.current = current;
    };

    const performScroll = () => {
      if (!scrollToId) {
        if (highlightIds && highlightIds.length > 0) {
          const scroller = findScrollContainer(docRootRef.current);
          if (scroller) {
            scroller.scrollTo({ top: 0, behavior: "auto" });
          }
        }
        return;
      }

      const { element: el } = findElementWithAlternatives(scrollToId);
      const scroller = findScrollContainer(docRootRef.current);

      if (!el || !scroller) {
        if (!el && scrollToId) {
          const targetObserver = new MutationObserver(() => {
            const { element: targetEl } =
              findElementWithAlternatives(scrollToId);
            if (targetEl && scroller) {
              targetObserver.disconnect();
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  const containerRect = scroller.getBoundingClientRect();
                  const elementRect = targetEl.getBoundingClientRect();
                  const scrollOffset = calculateScrollOffset(scroller);

                  const scrollTop =
                    scroller.scrollTop +
                    (elementRect.top - containerRect.top) -
                    scrollOffset;

                  scroller.scrollTo({
                    top: Math.max(0, scrollTop),
                    behavior: "smooth",
                  });
                });
              });
            }
          });

          const observeTarget = docRootRef.current || document.body;
          targetObserver.observe(observeTarget, {
            childList: true,
            subtree: true,
          });

          scrollRetryCountRef.current += 1;
          if (scrollRetryCountRef.current < 15) {
            const delay = Math.min(100 * scrollRetryCountRef.current, 1000);
            scrollTimeoutRef.current = setTimeout(() => {
              targetObserver.disconnect();
              performScroll();
            }, delay);
          } else {
            scrollRetryCountRef.current = 0;
            targetObserver.disconnect();
          }
        } else {
          scrollRetryCountRef.current += 1;
          if (scrollRetryCountRef.current < 10) {
            const delay = Math.min(100 * scrollRetryCountRef.current, 500);
            scrollTimeoutRef.current = setTimeout(() => {
              performScroll();
            }, delay);
          } else {
            scrollRetryCountRef.current = 0;
          }
        }
        return;
      }

      scrollRetryCountRef.current = 0;

      const applyScrollNow = () => {
        const containerRect = scroller.getBoundingClientRect();
        const elementRect = el.getBoundingClientRect();
        const scrollOffset = calculateScrollOffset(scroller);

        const scrollTop =
          scroller.scrollTop +
          (elementRect.top - containerRect.top) -
          scrollOffset;

        const maxScrollTop = Math.max(
          0,
          scroller.scrollHeight - scroller.clientHeight
        );
        const nextScrollTop = Math.min(maxScrollTop, Math.max(0, scrollTop));
        const delta = Math.abs(nextScrollTop - scroller.scrollTop);
        const behavior: ScrollBehavior =
          delta > scroller.clientHeight ? "auto" : "smooth";

        if (maxScrollTop <= 0) {
          // If the chosen container is not scrollable, fall back to native scroll discovery.
          el.scrollIntoView({
            behavior,
            block: "center",
            inline: "nearest",
          });
        } else {
          scroller.scrollTo({
            top: nextScrollTop,
            behavior,
          });
        }
      };

      // Wait for layout stability: elementTop + scrollHeight must settle
      const maxFrames = 60;
      const requiredStable = 6;
      let stableCount = 0;
      let frames = 0;
      let prevTop = el.getBoundingClientRect().top;
      let prevScrollHeight = scroller.scrollHeight;

      const waitForStableLayout = () => {
        frames += 1;
        const top = el.getBoundingClientRect().top;
        const scrollHeight = scroller.scrollHeight;
        const topDelta = Math.abs(top - prevTop);
        const heightDelta = Math.abs(scrollHeight - prevScrollHeight);

        const isStable = topDelta < 0.5 && heightDelta < 1;
        stableCount = isStable ? stableCount + 1 : 0;

        prevTop = top;
        prevScrollHeight = scrollHeight;

        if (stableCount >= requiredStable || frames >= maxFrames) {
          applyScrollNow();
          return;
        }

        requestAnimationFrame(waitForStableLayout);
      };

      requestAnimationFrame(waitForStableLayout);
    };

    const scheduleScrollToBlock = () => {
      if (suppressScrollToMatch) {
        return;
      }
      if (!scrollToId || isContentRendered) {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = null;
        }

        scrollRetryCountRef.current = 0;

        scrollTimeoutRef.current = setTimeout(() => {
          performScroll();
        }, 100);
      }
    };

    const scrollToPassage = () => {
      if (suppressScrollToMatch) {
        return;
      }
      if (passageRangesRef.current.length === 0) {
        scheduleScrollToBlock();
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Read late: a re-parse may have rebuilt the ranges since.
          const first = passageRangesRef.current[0];
          const range = first ? liveRangeFor(first) : null;
          if (range) scrollRangeIntoView(range, "smooth");
        });
      });
    };

    const runHighlightFlow = () => {
      applyBlockHighlights();
      if (!matchTrimmed) {
        scheduleScrollToBlock();
        return;
      }
      if (passageRangesRef.current.length === 0) {
        // Ids only: the passage is document content.
        logger.warn(
          "Reference passage not found in the document, falling back to its block",
          {
            operation: "highlightReferencePassage",
            highlightName,
            highlightIds,
            scrollToId,
          }
        );
      }
      scrollToPassage();
    };

    if (!scrollToId || isContentRendered) {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }

      scrollRetryCountRef.current = 0;

      scrollTimeoutRef.current = setTimeout(() => {
        runHighlightFlow();
      }, 100);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      for (const id of prevHighlightsRef.current) {
        const el = document.getElementById(id);
        if (el) removeBlockHighlightClasses(el);
      }
    };
  }, [
    highlightIds,
    scrollToId,
    docRootRef,
    isContentRendered,
    matchTrimmed,
    chunkTrimmed,
    highlightName,
    blockHighlightClassName,
    suppressScrollToMatch,
  ]);
}
